import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  AGENT_KINDS,
  apiServerOverviewQuery,
  dismissAllMoves,
  dismissMove,
  hostOf,
  moveFleet,
  MOVE_STATE_CLASS,
  MOVE_STATE_LABEL,
  type AgentKind,
} from '../../api/agentApiMove'
import { ApiError } from '../../api/client'
import { toast } from '../../ui/notify'

/**
 * Settings -> Agent Install -> "Agent API server". Numai pentru administratorii
 * de platforma: pentru ceilalti capatul raspunde 403 si sectiunea nu se
 * deseneaza deloc (nu e ascunsa cu CSS).
 *
 * Muta agentii Go ai firmei pe adresa din comenzile de instalare (comanda
 * `MVA`), pe feluri. "How many" lasa sa se incerce intai pe una-doua masini,
 * apoi pe tot felul. Masinile cu o mutare deja pe drum se sar pe server.
 *
 * Aparatele deja pe gazda tintei nu intra la "de mutat": serverul le numara
 * separat (`onTarget`, coloana "Already there") si nu le trimite `MVA`, deci
 * "Move all" si "How many" se socotesc numai pe celelalte.
 */
export function AgentApiServerSection() {
  const query = useQueryClient()
  const overview = useQuery(apiServerOverviewQuery)
  const [limit, setLimit] = useState('')
  const [busy, setBusy] = useState<AgentKind | 'all' | ''>('')
  const [dismissing, setDismissing] = useState(false)

  const data = overview.data
  /* Fara drept (403), sau un raspuns care nu e al capatului asta: nimic. */
  if (!data || !data.counts || !Array.isArray(data.moves)) return null

  const total = AGENT_KINDS.reduce((sum, k) => sum + (data.counts[k.kind] ?? 0), 0)
  const there = (kind: AgentKind) => data.onTarget?.[kind] ?? 0
  const totalThere = AGENT_KINDS.reduce((sum, k) => sum + there(k.kind), 0)
  const howMany = Math.max(0, parseInt(limit, 10) || 0)

  const move = async (kind: AgentKind | 'all', label: string, count: number) => {
    const n = howMany > 0 ? Math.min(howMany, count) : count
    if (!window.confirm(`Move ${n} ${label} agent${n === 1 ? '' : 's'} to ${data.target}?\n\nEach machine will talk to this API server from now on.`)) {
      return
    }
    setBusy(kind)
    try {
      const res = await moveFleet(kind, howMany)
      if (!res.ok) {
        toast(res.error, 'Agent API server', 'error')
        return
      }
      const skipped = res.skipped > 0 ? `, ${res.skipped} already waiting` : ''
      const already = (res.already ?? 0) > 0 ? `, ${res.already} already on ${hostOf(res.target)}` : ''
      toast(`${res.queued} queued${skipped}${already}.`, 'Agent API server')
      await query.invalidateQueries({ queryKey: apiServerOverviewQuery.queryKey })
    } catch (error) {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Agent API server', 'error')
    } finally {
      setBusy('')
    }
  }

  /* Dismiss: randul iese din lista, istoricul ramane pe server. */
  const dismiss = async (run: () => Promise<{ ok: true; dismissed: number } | { ok: false; error: string }>) => {
    setDismissing(true)
    try {
      const res = await run()
      if (!res.ok) {
        toast(res.error, 'Agent API server', 'error')
        return
      }
      await query.invalidateQueries({ queryKey: apiServerOverviewQuery.queryKey })
    } catch (error) {
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Agent API server', 'error')
    } finally {
      setDismissing(false)
    }
  }
  const okCount = data.moves.filter((m) => m.state === 'ok').length

  return (
    <div className="od-da-card" id="odDaApiServer" style={{ marginTop: 8 }}>
      <h4>
        <i className="fas fa-exchange-alt"></i> Agent API server
      </h4>
      <p className="od-da-muted">
        Moves this company&apos;s agents to <code id="odDaApiTarget">{data.target || '(empty)'}</code> &mdash; the address
        the install commands above use. Try one or two machines first with <strong>How many</strong>, then the rest.
        Platform admins only.
      </p>
      {data.target === '' ? (
        <p className="od-da-warn">
          The API address is empty on this installation. Check <code>API_SERVER</code>.
        </p>
      ) : null}
      <div className="form-inline" style={{ margin: '8px 0' }}>
        <label htmlFor="odDaApiLimit" style={{ fontWeight: 'normal', marginRight: 6 }}>
          How many
        </label>
        <input
          id="odDaApiLimit"
          className="form-control input-sm"
          type="number"
          min={0}
          placeholder="all"
          style={{ width: 80 }}
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
        />
      </div>
      <table className="table table-condensed" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th></th>
            <th style={{ textAlign: 'right' }}>To move</th>
            <th style={{ textAlign: 'right' }}>Already there</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {AGENT_KINDS.map((k) => {
            const count = data.counts[k.kind] ?? 0
            return (
              <tr key={k.kind} data-kind={k.kind}>
                <td>
                  <i className={k.icon}></i> {k.label}
                </td>
                <td style={{ textAlign: 'right' }} data-col="move">
                  {count}
                </td>
                <td style={{ textAlign: 'right' }} className="od-da-muted" data-col="there">
                  {there(k.kind)}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn btn-default btn-xs"
                    disabled={count === 0 || busy !== '' || data.target === ''}
                    onClick={() => void move(k.kind, k.label, count)}
                  >
                    Move to {hostOf(data.target)}
                  </button>
                </td>
              </tr>
            )
          })}
          <tr>
            <td>
              <strong>All agents</strong>
            </td>
            <td style={{ textAlign: 'right' }}>
              <strong>{total}</strong>
            </td>
            <td style={{ textAlign: 'right' }} className="od-da-muted" id="odDaApiThere">
              {totalThere}
            </td>
            <td style={{ textAlign: 'right' }}>
              <button
                type="button"
                className="btn btn-primary btn-xs"
                id="odDaApiMoveAll"
                disabled={total === 0 || busy !== '' || data.target === ''}
                onClick={() => void move('all', 'Go', total)}
              >
                Move all to {hostOf(data.target)}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p className="od-da-muted" style={{ margin: 0 }}>
        {data.inFlight > 0 ? <>{data.inFlight} waiting for the agent. </> : null}
        {data.classic > 0 ? (
          <>
            {data.classic} classic 7.x agent{data.classic === 1 ? '' : 's'} cannot move &mdash; reinstall with the command
            above.
          </>
        ) : null}
      </p>
      {data.moves.length > 0 ? (
        <div style={{ marginTop: 10, textAlign: 'right' }}>
          <button
            type="button"
            className="btn btn-default btn-xs"
            id="odDaApiDismissOk"
            disabled={dismissing || okCount === 0}
            onClick={() => void dismiss(() => dismissAllMoves(true))}
          >
            Dismiss all OK
          </button>{' '}
          <button
            type="button"
            className="btn btn-default btn-xs"
            id="odDaApiDismissAll"
            disabled={dismissing}
            onClick={() => {
              if (window.confirm('Remove every row from this list?\n\nThe history stays; a new move shows the machine again.')) {
                void dismiss(() => dismissAllMoves(false))
              }
            }}
          >
            Dismiss all
          </button>
        </div>
      ) : null}
      {data.moves.length > 0 ? (
        <table className="table table-condensed" id="odDaApiMoves" style={{ fontSize: 12, marginTop: 4 }}>
          <thead>
            <tr>
              <th>Machine</th>
              <th>Target</th>
              <th>State</th>
              <th>Asked</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.moves.map((m) => (
              <tr key={m.id}>
                <td>{m.deviceName !== '' ? m.deviceName : `#${m.deviceId}`}</td>
                <td>{hostOf(m.url)}</td>
                <td>
                  <span className={MOVE_STATE_CLASS[m.state]}>{MOVE_STATE_LABEL[m.state]}</span>
                  {m.message !== '' && m.state !== 'ok' ? <div className="od-da-muted">{m.message}</div> : null}
                </td>
                <td>{m.created.slice(0, 16)}</td>
                <td style={{ textAlign: 'right' }}>
                  <button
                    type="button"
                    className="btn btn-link btn-xs"
                    data-dismiss={m.id}
                    title="Remove this row from the list"
                    disabled={dismissing}
                    onClick={() => void dismiss(() => dismissMove(m.id))}
                  >
                    Dismiss
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  )
}
